/**
 * Shared types and interfaces for PS Agent implementations
 * Used by both ElevenLabs and OpenAI Realtime API implementations
 */

export enum CallState {
  INCOMING = 'incoming',
  ACTIVE = 'active',
  ENDED = 'ended',
}

export enum LoadingState {
  INITIAL = 'initial',
  CONNECTING = 'connecting',
  READY = 'ready',
  ACTIVE = 'active',
  ENDING = 'ending',
  ERROR = 'error',
}

export enum EngineType {
  ELEVENLABS = '11labs',
  OPENAI_REALTIME = 'realtime',
  LIVEKIT = 'livekit',
}

export interface Transcript {
  source: string;
  message: string;
  timestamp: Date;
}

export interface SurveyProgress {
  current: number;
  total: number;
}

export interface AgentInfo {
  profile: {
    name: string;
    role: string;
    avatar: string;
  };
}

export interface UsePSAgentCallProps {
  userId?: string;
  version?: number;
  lang?: string;
  engine?: EngineType;
  provider?: string; // AI provider override: 'openai', 'xai', 'gemini', etc.
  voice?: string; // Voice ID override for TTS (e.g., Cartesia voice UUID)
  onCallEnded?: () => void;
}

export interface PSAgentCallReturn {
  loadingState: LoadingState;
  error: string | null;
  callState: CallState;
  sessionId: string | null;
  conversationId: string | null;
  agentInfo: AgentInfo;
  callDuration: number;
  transcript: Transcript[];
  isSpeaking: boolean;
  currentQuestion: string;
  surveyProgress: SurveyProgress;
  handleAnswerCall: () => void;
  handleEndCall: () => void;
  formatTime: (seconds: number) => string;
  retryInitialization?: () => void;
  getConversationHistoryItemIds?: () => Promise<string[]>;
}

// Language-specific templates
export const LANGUAGE_TEMPLATES = {
  en: {
    initialGreeting: 'Good morning, may I speak to Mr. Wong please?',
    introduction:
      "My name is Mei, I'm calling on behalf of Prudential Singapore.",
    consent:
      'This call is regarding your signing of policy(s) from Prudential, and if you have a few minutes to spare, we would like to hear from the quality of the service that our FC has provided.',
    callbackRequest:
      'May I know when would be a good time to call you back again?',
    verification: {
      intro:
        'Thank you. For verification purpose, may I have your full date of birth please?',
      postalCode: 'May I have your postal code please?',
      explanation:
        'As I might be disclosing some of your policy details, I will need to ensure that I am speaking with the right person.',
    },
    recording:
      'Thank you Mr. Wong. Before I proceed, I wish to inform you that this call is recorded for quality and training purposes.',
    closing:
      'Mr. Wong, we have come to the end of the survey. Thank you for taking the time to speak to me. Should you need any assistance from us, please contact your financial consultant or our Customer Service Hotline at 1800-333-0-333. Thank you once again and have a nice day.',
  },
  zh: {
    initialGreeting: '早上好，请问我可以和黄先生通话吗？',
    introduction: '我叫Mei，我代表新加坡保诚公司给您打电话。',
    consent:
      '这通电话是关于您在保诚签署的保单，如果您有几分钟的时间，我们希望了解我们的理财顾问为您提供的服务质量。',
    callbackRequest: '请问什么时候方便我再给您回电？',
    verification: {
      intro: '谢谢。为了验证身份，请问您能告诉我您的完整出生日期吗？',
      postalCode: '请问您的邮政编码是多少？',
      explanation:
        '由于我可能会透露您的一些保单详情，我需要确认我正在与正确的人通话。',
    },
    recording:
      '谢谢您，黄先生。在我继续之前，我想告知您这通电话会被录音，用于质量和培训目的。',
    closing:
      '黄先生，我们的调查到此结束。感谢您抽出时间与我通话。如果您需要任何协助，请联系您的理财顾问或我们的客服热线1800-333-0-333。再次感谢您，祝您有美好的一天。',
  },
  ms: {
    initialGreeting: 'Selamat pagi, bolehkah saya bercakap dengan Encik Wong?',
    introduction:
      'Nama saya Mei, saya menghubungi atas nama Prudential Singapore.',
    consent:
      'Panggilan ini berkaitan dengan polisi yang telah anda tandatangani dengan Prudential, dan jika anda mempunyai beberapa minit, kami ingin mendengar tentang kualiti perkhidmatan yang diberikan oleh FC kami.',
    callbackRequest:
      'Bolehkah saya tahu bila masa yang sesuai untuk saya menghubungi anda semula?',
    verification: {
      intro:
        'Terima kasih. Untuk tujuan pengesahan, bolehkah saya mendapat tarikh lahir penuh anda?',
      postalCode: 'Bolehkah saya mendapat poskod anda?',
      explanation:
        'Memandangkan saya mungkin akan mendedahkan beberapa butiran polisi anda, saya perlu memastikan bahawa saya bercakap dengan orang yang betul.',
    },
    recording:
      'Terima kasih Encik Wong. Sebelum saya meneruskan, saya ingin memberitahu anda bahawa panggilan ini dirakam untuk tujuan kualiti dan latihan.',
    closing:
      'Encik Wong, kami telah sampai ke penghujung survei. Terima kasih kerana meluangkan masa untuk bercakap dengan saya. Sekiranya anda memerlukan sebarang bantuan daripada kami, sila hubungi perunding kewangan anda atau Talian Khidmat Pelanggan kami di 1800-333-0-333. Terima kasih sekali lagi dan semoga hari anda menyenangkan.',
  },
};

// PS Agent Configuration
export const PS_CONFIG = {
  agentId: 'agent_01k03xhsgnee786p471adr8w6w', // Post-Sales Survey Agent (version 1)
  agentIdV2: 'agent_4401k35q3nf5ersrhep7v9aht6jg', // Post-Sales Survey Agent (version 2)
  agentIdV3: 'agent_4401k35q3nf5ersrhep7v9aht6jg', // Robocall Survey Agent (version 3) - using same agent as V2
  agentIDChinese: 'agent_5401k36dwvvvfnxrr18759brcdew',
  voiceId: 'ckdz71REaQKVx2gnOQjQ', // Malaysian female voice (for version 1)
  voiceIdMandarin: '9lHjugDhwqoxA5MhX0az', // Chinese Mandarin voice (for version 2)
  voiceIdV3: '6qpxBH5KUSDb40bij36w', // Singaporean female voice (legacy - not used)
  voiceIdV3English: 'aSXZu6bgEOS8MXVRzjPi', // Singaporean male voice for V3 English (ElevenLabs)
  voiceIdV3Chinese: 'xqnADkzSQVgupDtxtltF', // Singapore Mandarin voice for V3 (ElevenLabs)
  avatar:
    'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
  avatarV3:
    'https://dopmo1eihgbgm.cloudfront.net/679127688b20f30484d209eb/alex-headshot.jpg', // Male avatar for V3 (ElevenLabs)
  avatarV3Realtime:
    'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp', // Female avatar for V3 (OpenAI Realtime)
  nameEnglish: 'Raihana',
  nameChinese: 'Mei',
  nameV3: 'Alex', // Male agent name for version 3 (ElevenLabs)
  nameV3Realtime: 'Rachel', // Female agent name for version 3 (OpenAI Realtime)
  // Version 4: Taiwan Outbound Cold Call
  agentIdV4: 'agent_6701keh829sff72r4hdka0m7zh19', // Taiwan Outbound Cold Call Agent
  voiceIdV4: '6qpxBH5KUSDb40bij36w', // Taiwan female voice for V4 (English)
  voiceIdV4Chinese: 'Z8Aisvg1z70p27kGvkZZ', // Taiwan female voice for V4 (Traditional Chinese)
  nameV4: 'Angeline', // Female agent name for version 4 (Taiwan)
  // Version 5: Korean Outbound Call (Human Non-Life Insurance)
  agentIdV5: 'agent_6601khx77gw6e1btk8czk6m95ga8', // Korean Outbound Call Agent (ElevenLabs)
  voiceIdV5: 'uyVNoMrnUku1dZyVEXwD', // Korean female voice for V5 (ElevenLabs)
  nameV5: '이지선', // Lee Jisun - Korean AI assistant name
  avatarV5:
    'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
  // Version 6: Japanese Outbound Call (Nihon Sonpo / 日本損保)
  agentIdV6: 'agent_2301kp80tdbkfk7tfexexwfa659h', // Japanese Outbound Call Agent (ElevenLabs)
  voiceIdV6: 'urE3OJfJRxJuk9kAMN0Y', // Japanese male voice for V6 (ElevenLabs)
  nameV6: 'ケンジ', // Kenji - Japanese AI assistant name
  avatarV6:
    'https://dopmo1eihgbgm.cloudfront.net/69df3e4c6b99428e172fc677/aaaaa.png',
  // Version 7: Prudential TW - New-Contract Pre-Underwriting Verification Call (CS-155WI)
  agentIdV7: 'agent_6701keh829sff72r4hdka0m7zh19', // reuse V4 Taiwan agent (overrides enabled)
  voiceIdV7: 'w9Q8NyJn1bQis1tu6pmS', // Prudential TW demo voice (Traditional Chinese)
  nameV7: '林專員', // Prudential Life telephone interviewer
  avatarV7:
    'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
  // Version 8: Prudential TW - Renewal Payer (65+) Premium-Deduction Confirmation
  agentIdV8: 'agent_6701keh829sff72r4hdka0m7zh19', // reuse V4 Taiwan agent (overrides enabled)
  voiceIdV8: 'w9Q8NyJn1bQis1tu6pmS', // Prudential TW demo voice (Traditional Chinese)
  nameV8: '林專員', // Prudential Life telephone interviewer
  avatarV8:
    'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
  language: 'en',
};

// Voice ID mapping for different languages (ElevenLabs)
export const VOICE_ID_MAP: Record<string, string> = {
  en: '6qpxBH5KUSDb40bij36w', // Singaporean female voice (English)
  zh: '9lHjugDhwqoxA5MhX0az', // Chinese Mandarin voice
  ms: 'ckdz71REaQKVx2gnOQjQ', // Malaysian voice (can be used for Malay)
  ko: 'fNmw8sukfGuvWVOp33Ge', // Korean female voice (for V5)
  ja: 'urE3OJfJRxJuk9kAMN0Y', // Japanese male voice (for V6)
};

// OpenAI Realtime Voice Configuration
export const OPENAI_REALTIME_VOICES = {
  alloy: 'alloy', // Neutral, balanced
  echo: 'echo', // Male voice
  fable: 'fable', // British accent
  onyx: 'onyx', // Deep male voice
  nova: 'nova', // Young female voice
  shimmer: 'shimmer', // Soft female voice
};

// Get time-based greeting
export const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

// Common utility functions
export const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};
